/**
 * Supabase Base Query for RTK Query
 * 
 * This is one of the few places where direct `supabase.*` usage is allowed.
 * See RTK_QUERY_GUARDRAILS.md for details.
 * 
 * All feature code should use RTK Query hooks instead of direct Supabase calls.
 */
import { BaseQueryFn } from '@reduxjs/toolkit/query';
import { PostgrestError } from '@supabase/supabase-js';

declare global {
  interface Window { __fw_metrics?: any }
}

function recordMetric(key: string, ms: number, err?: any) {
  try {
    const m = (window.__fw_metrics = window.__fw_metrics || { calls: 0, errors: 0, slow: 0, last: [], byEndpoint: {} });
    m.calls += 1;
    if (err) m.errors += 1;
    if (ms >= 1500) m.slow += 1;
    m.last.push({ key, ms, t: Date.now(), err: !!err });
    if (m.last.length > 50) m.last.shift();
    const ep = (m.byEndpoint[key] = m.byEndpoint[key] || { count: 0, errors: 0, avg: 0, p95: 0, last: [] });
    ep.count += 1;
    if (err) ep.errors += 1;
    ep.last.push(ms);
    if (ep.last.length > 40) ep.last.shift();
    const arr = ep.last as number[];
    const sorted = [...arr].sort((a, b) => a - b);
    ep.avg = Math.round(arr.reduce((a, b) => a + b, 0) / arr.length);
    ep.p95 = sorted[Math.floor(sorted.length * 0.95)] ?? sorted[sorted.length - 1] ?? ms;
  } catch { }
}
import { supabase } from '@flexwise/shared/supabase';
import { getKioskSupabaseClient, getKioskToken } from '../../app/kiosk/utils/kioskUtils';

/**
 * Pick the right Supabase client for the current request.
 *
 * In paired-kiosk mode, the device has NO logged-in browser user — it only
 * holds the machine-scope kiosk JWT in localStorage (`fw_kiosk_jwt`).
 * Without this switch, every RTK Query call would go out under the
 * bootstrap anon JWT, and every `kiosk`-only RPC grant would return
 * "permission denied for function ...".
 *
 * `useChipLoginCall` already swaps to the kiosk client by hand for
 * fn_kiosk_chip_login; doing the swap here means every other kiosk RPC
 * (fn_klassenbuch_get_class_week, etc.) routes correctly too without
 * each call site having to know.
 *
 * Authenticated user sessions still take precedence so the dev/admin
 * browser-tab flow (kiosk page opened under a teacher's login) continues
 * to use the user's JWT — which is the only way to call RPCs gated to
 * `auth.uid()` semantics until those get a kiosk-mode signature.
 *
 * Cheap to call: getKioskToken is a localStorage read,
 * getKioskSupabaseClient memoises by JWT.
 */
function pickClient() {
  if (typeof window === 'undefined') return supabase;
  // If a user is signed in (Supabase auth session present), keep using the
  // shared client. This covers the admin/dev browser-tab path where the
  // teacher is logged in normally — even on a machine that ALSO happens
  // to have a kiosk JWT cached.
  try {
    const host = (typeof location !== 'undefined') ? location.hostname.split('.')[0] : '';
    if (host) {
      const sessionKey = `sb-${host}-auth-token`;
      if (window.localStorage.getItem(sessionKey)) return supabase;
    }
  } catch { /* fall through to kiosk check */ }
  // No user session — fall through to the kiosk client when a kiosk JWT is
  // available. Returns null if no kiosk JWT either; we fall back to the
  // anon-bootstrap client which is correct for unauthenticated marketing
  // pages, password reset flows, etc.
  if (!getKioskToken()) return supabase;
  return getKioskSupabaseClient() ?? supabase;
}

interface SupabaseQueryArgs {
  type: 'select' | 'insert' | 'update' | 'delete' | 'rpc' | 'upsert';
  table?: string;
  rpc?: string;
  params?: Record<string, any>;
  match?: Record<string, any>;
}

type PlainError = Pick<PostgrestError, 'message' | 'details' | 'hint' | 'code'>;

function normalizeError(err: unknown): PlainError {
  const e = err as any;
  return {
    message: e?.message ?? 'Unknown error',
    details: e?.details ?? null,
    hint: e?.hint ?? null,
    code: e?.code ?? 'client_error',
  };
}

function applyFilters<T>(q: any, params?: Record<string, any>, match?: Record<string, any>) {
  const RESERVED = new Set(['select', 'order', 'limit', 'single', 'search', 'data']);
  const apply = (key: string, value: any) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) {
      q = q.in(key, value);
    } else if (typeof value === 'object' && value) {
      // Support simple operators { eq, neq, gt, gte, lt, lte }
      if ('eq' in value) q = q.eq(key, value.eq);
      if ('neq' in value) q = q.neq(key, value.neq);
      if ('gt' in value) q = q.gt(key, value.gt);
      if ('gte' in value) q = q.gte(key, value.gte);
      if ('lt' in value) q = q.lt(key, value.lt);
      if ('lte' in value) q = q.lte(key, value.lte);
      if ('in' in value) q = q.in(key, value.in);
      if ('is' in value) q = q.is(key, value.is);
      if ('like' in value) q = q.like(key, value.like);
      if ('ilike' in value) q = q.ilike(key, value.ilike);
      if ('contains' in value) q = q.contains(key, value.contains);
    } else {
      q = q.eq(key, value);
    }
  };

  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (RESERVED.has(k)) return;
      apply(k, v);
    });

    // Search helper: { column, value, operator: 'ilike' | 'like' }
    if (params.search && params.search.column && params.search.value) {
      const { column, value, operator } = params.search;
      if (operator === 'like') q = q.like(column, value);
      else q = q.ilike(column, value);
    }

    // Order: 'col', 'col.desc', 'a, b.desc'
    if (params.order) {
      const orders = Array.isArray(params.order)
        ? params.order
        : String(params.order).split(',').map((s: string) => s.trim()).filter(Boolean);
      orders.forEach((ord: string | { [k: string]: { ascending?: boolean } }) => {
        if (typeof ord === 'string') {
          const [col, dir] = ord.split('.');
          q = q.order(col.trim(), { ascending: dir?.toLowerCase() !== 'desc' });
        } else {
          Object.entries(ord).forEach(([col, opts]) => {
            q = q.order(col, { ascending: opts?.ascending !== false });
          });
        }
      });
    }

    if (typeof params.limit === 'number') {
      q = q.limit(params.limit);
    }
  }

  if (match) {
    Object.entries(match).forEach(([k, v]) => apply(k, v));
  }

  return q;
}

let __fw_concurrent = 0;
const __fw_waiters: Array<() => void> = [];
async function __fw_acquire(max = 6) {
  if (__fw_concurrent < max) { __fw_concurrent++; return; }
  await new Promise<void>(res => __fw_waiters.push(() => { __fw_concurrent++; res(); }));
}
function __fw_release() {
  __fw_concurrent = Math.max(0, __fw_concurrent - 1);
  const next = __fw_waiters.shift();
  if (next) next();
}

export const supabaseBaseQuery: BaseQueryFn<SupabaseQueryArgs, unknown, PlainError> =
  async (arg: any) => {
    await __fw_acquire(6);
    try {
      let { type, table, rpc, params, match, ...legacy } = arg || {};

      // Backward compatibility: legacy 'table' style with 'operation', 'select', 'filters', etc.
      if (type === 'table') {
        const op = legacy.operation || 'select';
        type = op;
        // Merge legacy top-level fields into params/match
        params = {
          ...(params || {}),
          ...(legacy.select ? { select: legacy.select } : {}),
          ...(legacy.limit ? { limit: legacy.limit } : {}),
          ...(legacy.single ? { single: legacy.single } : {}),
          ...(legacy.order ? { order: legacy.order } : {}),
          ...(legacy.id ? { id: legacy.id } : {}),
          ...(legacy.search ? { search: legacy.search } : {}),
          ...(legacy.data ? { data: legacy.data } : {}),
        };
        match = {
          ...(match || {}),
          ...(legacy.filters || {}),
        };
      }

      const client = pickClient();

      if (type === 'rpc' && rpc) {
        const t0 = performance.now();
        const { data, error } = await client.rpc(rpc, params);
        const dt = performance.now() - t0;
        recordMetric(`rpc:${rpc}`, dt, error);
        if (error) throw error;
        return { data };
      }
      if (!table) throw new Error('table name required for non-RPC queries');

      switch (type) {
        case 'select': {
          let q = client.from(table).select(params?.select || '*');
          q = applyFilters(q, params, match);
          if (params?.single) {
            const t0s = performance.now();
            const { data, error } = await q.single();
            const dts = performance.now() - t0s;
            recordMetric(`select:${table}`, dts, error);
            if (error) throw error;
            return { data };
          }
          const t0m = performance.now();
          const { data, error } = await q;
          const dt = performance.now() - t0m;
          recordMetric(`select:${table}`, dt, error);
          if (error) throw error;
          return { data };
        }
        case 'insert': {
          const t0i = performance.now();
          const { data, error } = await client.from(table).insert(params?.data ?? params ?? {});
          const dti = performance.now() - t0i;
          recordMetric(`insert:${table}`, dti, error);
          if (error) throw error;
          return { data };
        }
        case 'update': {
          let q = client.from(table).update(params?.data ?? params ?? {});
          q = applyFilters(q, undefined, match ?? params);
          const t0u = performance.now();
          const { data, error } = await q;
          const dtu = performance.now() - t0u;
          recordMetric(`update:${table}`, dtu, error);
          if (error) throw error;
          return { data };
        }
        case 'delete': {
          let q = client.from(table).delete();
          q = applyFilters(q, undefined, match ?? params);
          const t0d = performance.now();
          const { data, error } = await q;
          const dtd = performance.now() - t0d;
          recordMetric(`delete:${table}`, dtd, error);
          if (error) throw error;
          return { data };
        }
        case 'upsert': {
          const t0up = performance.now();
          // Upsert takes data and options (like onConflict)
          // The base query contract for params usually puts data in 'data' or top level
          const pay = params?.data ?? params ?? {};
          // Clean non-data params if mixed? 
          // For now assume standard usage: { data: {...}, onConflict: '...' } passed via params?
          // Or params IS data and options is separate? 
          // Supabase JS upsert(data, options).
          // Our base query args: params is usually the payload or filters.
          // Let's assume params contains { data: ..., ...options } or just data.
          // If params has 'data' key, use it. Else validation might be tricky.
          // Let's stick to: params = data, separate options? 
          // The BaseQueryArgs has params, match. 
          // Let's look at `upsertChildLink` usage: 
          // params: { family_id, ..., school_id } -> This is DATA.
          // It didn't pass options.
          // So: supabase.from(table).upsert(params) should work.

          // But wait, upsertChildLink in adapter used { onConflict: '...' }.
          // In my api slice I didn't pass onConflict.
          // I should probably update api slice to pass onConflict if needed, 
          // or rely on default (PK).
          // For now, simple implementation:
          const { onConflict, ignoreDuplicates, count, ...rest } = params || {};
          const options = { onConflict, ignoreDuplicates, count };
          // If data is explicit:
          const payload = params?.data ?? rest;

          const { data, error } = await client.from(table).upsert(payload, options);
          const dtup = performance.now() - t0up;
          recordMetric(`upsert:${table}`, dtup, error);
          if (error) throw error;
          return { data };
        }
        default:
          throw new Error(`Unsupported Supabase operation: ${type}`);
      }
    } catch (error) {
      return { error: normalizeError(error) };
    } finally {
      __fw_release();
    }
  };
